import { ReactNode, useCallback, useLayoutEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useCheckCreateNft } from 'hooks/account/useCheckCreateNft';
import { useIsLoggedIn } from 'hooks/user/useIsLoggedIn';
import { EAccountRouteKeys } from 'constants/account/account-route-keys';

type ProtectedRouteProviderProps = {
  protectedRoutes: Record<string, Record<'exclude', string[]>>;
  children: ReactNode;
};

export const ProtectedRouteProvider = ({
  protectedRoutes,
  children
}: ProtectedRouteProviderProps) => {
  useCheckCreateNft();
  const history = useHistory();
  const location = useLocation();
  const {
    isLoggedIn: initialIsLoggedIn,
    isLoginPending: initialIsLoginPending
  } = useIsLoggedIn();
  const [isLoggedIn, setIsLoggedIn] = useState(initialIsLoggedIn);
  const [isLoginPending, setIsLoginPending] = useState(initialIsLoginPending);

  const isPathProtected = useCallback(
    (pathname?: string) => {
      const protectedRoutesKeys = Object.keys(protectedRoutes);
      const pathnameToCheck = pathname || location.pathname;

      return protectedRoutesKeys.some(route => {
        const isPath = pathnameToCheck.startsWith(route);
        const hasExcludedPaths = protectedRoutes[route].exclude;
        if (isPath && !hasExcludedPaths) {
          return true;
        }
        if (isPath && hasExcludedPaths) {
          return !hasExcludedPaths.some(excludedPath =>
            pathnameToCheck.startsWith(excludedPath)
          );
        }
        return false;
      });
    },
    [protectedRoutes, location.pathname]
  );

  useLayoutEffect(() => {
    // Check if the current path is a protected route and redirect if necessary
    if (isPathProtected() && !isLoginPending && !isLoggedIn) {
      history.replace(EAccountRouteKeys.ACCOUNT_AUTH);
    }
  }, [isPathProtected, isLoginPending, isLoggedIn]);

  useLayoutEffect(() => {
    /*
     * Check authentication status for correct values before redirection in case
     * they change
     */
    setIsLoggedIn(initialIsLoggedIn);
    setIsLoginPending(initialIsLoginPending);
  }, [initialIsLoggedIn, initialIsLoginPending]);

  /*
   * ATTENTION: one of the effects was disabled. This is because isLoggedIn and
   * isLoginPending properties are not up to date when listening to history
   * changes. In case of need, should be reverted and resolved issue of
   * properties not being up to date
   */

  return children as JSX.Element | null;
};
